import { authEnvironment, safeRemove, type AuthChannel, type AuthEnvironment } from './environment';
import { authPingKey, type AuthNamespace } from './namespace';

/** События сессии между вкладками одного приложения. */
export type AuthBroadcastEvent =
    | { type: 'tokens-updated'; accessToken: string; refreshToken: string }
    /**
     * Токены сменились в соседней вкладке, событие их не несёт: так приходит `tokens-updated` запасным путём через
     * localStorage, где их увидел бы любой скрипт страницы. В body-режиме токены уже в общем localStorage; в
     * cookie-режиме access получают ротацией под Web Lock — refresh-cookie у вкладок общий.
     */
    | { type: 'tokens-changed' }
    | { type: 'logged-out' };

export type AuthBroadcastListener = (event: AuthBroadcastEvent) => void;

export interface AuthBroadcastOptions {
    namespace: Pick<AuthNamespace, 'broadcastChannel'>;
    env?: Partial<Pick<AuthEnvironment, 'createChannel' | 'storage' | 'onStorage' | 'now'>>;
}

export interface AuthBroadcast {
    /** Открыть канал и слушать запасной путь; повторный вызов — no-op. */
    start(): void;
    /** Закрыть канал, снять слушателя storage и подписчиков. До `start()` и после `stop()` экземпляр молчит. */
    stop(): void;
    /** Соседним вкладкам; себе событие не приходит. */
    publish(event: AuthBroadcastEvent): void;
    subscribe(listener: AuthBroadcastListener): () => void;
}

const isAuthBroadcastEvent = (value: unknown): value is AuthBroadcastEvent => {
    if (!value || typeof value !== 'object') return false;
    const event = value as Record<string, unknown>;
    if (event.type === 'tokens-updated') {
        return typeof event.accessToken === 'string' && typeof event.refreshToken === 'string';
    }
    return event.type === 'tokens-changed' || event.type === 'logged-out';
};

/** Запасной путь пишет событие в localStorage — токенов в нём быть не должно. */
const withoutTokens = (event: AuthBroadcastEvent): AuthBroadcastEvent =>
    event.type === 'tokens-updated' ? { type: 'tokens-changed' } : event;

/**
 * Синхронизация сессии между вкладками: BroadcastChannel, а без него (или если `postMessage` бросил) — `storage`-
 * событие по ключу `authPingKey(namespace)`. Прямые изменения ключей токенов не слушаются: ручное удаление токена в
 * DevTools иначе сразу выглядело бы выходом и не давало интерцептору тихо обновить токен.
 */
export function createAuthBroadcast({ namespace, env }: AuthBroadcastOptions): AuthBroadcast {
    const resolved = authEnvironment(env);
    const pingKey = authPingKey(namespace);
    const listeners = new Set<AuthBroadcastListener>();
    let active: { channel: AuthChannel | null; detach: () => void } | null = null;

    const deliver = (data: unknown) => {
        if (!isAuthBroadcastEvent(data)) return;
        listeners.forEach((listener) => {
            try {
                listener(data);
            } catch {
                // упавший подписчик не мешает остальным
            }
        });
    };

    const publishViaStorage = (event: AuthBroadcastEvent) => {
        const storage = resolved.storage;
        try {
            storage?.setItem(pingKey, JSON.stringify({ ts: resolved.now(), event: withoutTokens(event) }));
        } catch {
            // хранилище недоступно — соседние вкладки событие не получат
        } finally {
            // Соседние вкладки получают значение в самом `storage`-событии, поэтому удаление сразу его не отменяет, а
            // запись не переживает вызов: ни после выхода, ни после закрытия вкладки ключа нет.
            safeRemove(storage, pingKey);
        }
    };

    return {
        start() {
            if (active) return;
            const channel = resolved.createChannel(namespace.broadcastChannel);
            const onMessage = (e: { data: unknown }) => deliver(e.data);
            channel?.addEventListener('message', onMessage);
            const offStorage = resolved.onStorage((key, newValue) => {
                if (key !== pingKey || !newValue) return;
                let event: unknown;
                try {
                    event = (JSON.parse(newValue) as { event?: unknown }).event;
                } catch {
                    return;
                }
                deliver(event);
            });
            // Прежние версии оставляли последнее событие запасного пути — с токенами — в localStorage навсегда.
            safeRemove(resolved.storage, pingKey);
            active = {
                channel,
                detach: () => {
                    channel?.removeEventListener('message', onMessage);
                    try {
                        channel?.close();
                    } catch {
                        // уже закрыт
                    }
                    offStorage();
                },
            };
        },
        stop() {
            active?.detach();
            active = null;
            listeners.clear();
        },
        publish(event) {
            if (!active) return;
            if (active.channel) {
                try {
                    active.channel.postMessage(event);
                    return;
                } catch {
                    // канал закрыт или сообщение не клонируется — запасной путь
                }
            }
            publishViaStorage(event);
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
